import { useCallback, useEffect, useState } from 'react';

// Which columns of a wide table this person wants to see, remembered between
// visits.
//
// ⚠️ THE HIDDEN SET IS STORED, NEVER THE VISIBLE ONE, and the difference is the
// whole reason this file has a comment. Storing what to SHOW freezes the table
// at the moment somebody first touched the picker: every column added later is
// absent from their saved list, so it renders for new users and is invisible
// for everyone else — with no error, and no way for them to discover it exists
// short of pressing Reset. Storing what to HIDE means a new column is visible
// to everybody by default and only the deliberate hides survive.
//
// ⚠️ PER BROWSER, NOT PER ACCOUNT. This is a view preference on a wide table,
// not a setting the firm agrees on, and putting it on the server would mean a
// migration and an endpoint to save somebody a click on a second machine.
//
// ⚠️ EVERY ACCESS IS WRAPPED. localStorage THROWS rather than returning null in
// a locked-down browser or a private window — reading it unguarded takes the
// whole page down, which is a spectacular way to fail at remembering a
// checkbox.

const read = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    // Anything but an array of strings is somebody else's data or a half-written
    // value. Ignore it rather than letting it decide what the table shows.
    return Array.isArray(parsed) ? parsed.filter((k) => typeof k === 'string') : [];
  } catch {
    return [];
  }
};

const write = (key, keys) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(keys));
  } catch {
    // Out of quota, or storage denied. The preference is lost on reload and the
    // table still works — not worth interrupting anybody over.
  }
};

/**
 * @param {string} storageKey  stable per table, e.g. 'tally.cols.statement'
 * @returns {{ hidden: Set<string>, isVisible, toggle, showAll, hiddenCount }}
 */
export function useHiddenColumns(storageKey) {
  const [hidden, setHidden] = useState(() => new Set(read(storageKey)));

  // ⚠️ RE-READ WHEN THE KEY CHANGES. One component serves several tables (the
  // statement, the entries list), and without this, switching between them
  // would carry the first table's hidden columns onto the second and then save
  // them under its key.
  useEffect(() => { setHidden(new Set(read(storageKey))); }, [storageKey]);

  const persist = useCallback((next) => {
    setHidden(next);
    write(storageKey, [...next]);
  }, [storageKey]);

  const toggle = useCallback((key) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      write(storageKey, [...next]);
      return next;
    });
  }, [storageKey]);

  const showAll = useCallback(() => persist(new Set()), [persist]);

  return {
    hidden,
    isVisible: useCallback((key) => !hidden.has(key), [hidden]),
    toggle,
    showAll,
    hiddenCount: hidden.size,
  };
}

/**
 * Apply the preference to a column list.
 *
 * ⚠️ A COLUMN MARKED `always` CANNOT BE HIDDEN and is not offered in the
 * picker. Reserved for the one or two that make a row identifiable at all — on
 * a ledger statement, hiding both the date and the voucher leaves a page of
 * bare figures with nothing to say which transaction each belongs to.
 */
export function applyHidden(columns, hidden) {
  return columns.filter((c) => c.always || !hidden.has(c.key));
}
